import { useEffect, useRef, useState } from 'react'
import Icon from '../Icon'

export interface MultiSelectFilterOption<T extends string> {
  value: T
  label: string
}

interface MultiSelectFilterProps<T extends string> {
  /** Shown before the selection summary, e.g. "Status". */
  title: string
  options: MultiSelectFilterOption<T>[]
  selected: T[]
  /** Selected when the last option is unchecked — the filter never ends up empty. */
  fallback: T[]
  onChange: (selected: T[]) => void
}

/** Dropdown of checkboxes; the trigger summarizes what is selected. */
const MultiSelectFilter = <T extends string>({ title, options, selected, fallback, onChange }: MultiSelectFilterProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggle = (value: T) => {
    if (selected.includes(value)) {
      const next = selected.filter(item => item !== value)
      onChange(next.length > 0 ? next : fallback)
      return
    }

    onChange([...selected, value])
  }

  const summary =
    selected.length === options.length
      ? 'All'
      : selected.length === 1
        ? (options.find(option => option.value === selected[0])?.label ?? selected[0])
        : `${selected.length} selected`

  return (
    <div className="account-status-filter" ref={containerRef}>
      <button type="button" className="account-status-filter-trigger" onClick={() => setIsOpen(open => !open)}>
        <span>
          {title}: {summary}
        </span>
        <Icon name="CaretDown" />
      </button>

      {isOpen && (
        <div className="account-status-filter-menu">
          {options.map(option => (
            <label key={option.value} className="account-status-filter-option">
              <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default MultiSelectFilter
